import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '../components/auth/AuthPage/UserContext';
import './BinderContext.css';

/**
 * BinderContext - the logged-in user's haves/wants, shared app-wide.
 *
 * Powers the "ownership everywhere" model: any card grid can show
 * owned/wishlist badges, and quickAdd() gives one-click add-to-binder
 * with a toast + undo (the API upserts, so repeat clicks increment).
 */
const BinderContext = createContext(null);

export function BinderProvider({ children }) {
    const { user } = useUser();
    const [haves, setHaves] = useState([]);
    const [wants, setWants] = useState([]);
    const [toasts, setToasts] = useState([]);
    const toastId = useRef(0);

    const refresh = useCallback(async () => {
        if (!user) {
            setHaves([]);
            setWants([]);
            return;
        }
        try {
            const [h, w] = await Promise.all([
                fetch('/api/haves', { credentials: 'include' }).then(r => (r.ok ? r.json() : { haves: [] })),
                fetch('/api/wants', { credentials: 'include' }).then(r => (r.ok ? r.json() : { wants: [] })),
            ]);
            setHaves(h.haves || []);
            setWants(w.wants || []);
        } catch {
            // keep whatever we had
        }
    }, [user]);

    useEffect(() => { refresh(); }, [refresh]);

    const ownedQty = useCallback(
        (uuid) => haves.reduce((sum, r) => (r.uuid === uuid ? sum + (r.quantity || 0) : sum), 0),
        [haves]
    );
    const wantedQty = useCallback(
        (uuid) => wants.reduce((sum, r) => (r.uuid === uuid ? sum + (r.quantity || 0) : sum), 0),
        [wants]
    );

    const removeToast = (id) => setToasts(t => t.filter(x => x.id !== id));

    const pushToast = useCallback((message, undo = null) => {
        const id = ++toastId.current;
        setToasts(t => [...t.slice(-2), { id, message, undo }]);
        setTimeout(() => removeToast(id), 6000);
    }, []);

    /**
     * One-click add. type: 'have' | 'want'. Returns the updated row.
     * Repeat clicks increment quantity (server upserts +=).
     */
    const quickAdd = useCallback(async (type, card, finish) => {
        const endpoint = type === 'have' ? '/api/haves' : '/api/wants';
        const list = type === 'have' ? haves : wants;
        const setList = type === 'have' ? setHaves : setWants;
        const prev = list.find(r => r.uuid === card.uuid && r.finish === finish);
        const prevQty = prev ? prev.quantity : 0;

        const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                uuid: card.uuid,
                setcode: card.setCode || card.setcode,
                finish,
                quantity: 1,
                game: 'magic',
                card_name: card.name,
            }),
        });
        if (!res.ok) {
            pushToast('Could not add card. Are you logged in?');
            throw new Error('add failed');
        }
        const data = await res.json();
        const row = data.have || data.want;
        setList(l => {
            const rest = l.filter(r => r.id !== row.id);
            return [...rest, row];
        });

        const undo = async () => {
            try {
                if (prevQty > 0) {
                    await fetch(endpoint, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ uuid: card.uuid, finish, quantity: prevQty, game: 'magic' }),
                    });
                } else {
                    await fetch(endpoint, {
                        method: 'DELETE',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ uuid: card.uuid, finish, game: 'magic' }),
                    });
                }
                refresh();
            } catch { }
        };

        const finishNote = finish && finish !== 'nonfoil' ? ` (${finish})` : '';
        const label = type === 'have' ? 'collection' : 'wishlist';
        pushToast(`Added ${card.name}${finishNote} to ${label} · ×${row.quantity}`, undo);
        return row;
    }, [haves, wants, pushToast, refresh]);

    return (
        <BinderContext.Provider value={{ haves, wants, ownedQty, wantedQty, quickAdd, refresh, pushToast }}>
            {children}
            {toasts.length > 0 && (
                <div className="binder-toast-stack">
                    {toasts.map(t => (
                        <div className="binder-toast" key={t.id}>
                            <span className="binder-toast-msg">{t.message}</span>
                            {t.undo && (
                                <button
                                    className="binder-toast-undo"
                                    onClick={() => { t.undo(); removeToast(t.id); }}
                                >
                                    Undo
                                </button>
                            )}
                            <button className="binder-toast-close" onClick={() => removeToast(t.id)}>×</button>
                        </div>
                    ))}
                </div>
            )}
        </BinderContext.Provider>
    );
}

// Null-safe: components can render outside the provider (returns null)
export function useBinder() {
    return useContext(BinderContext);
}
